/**
 * Web-only corrections to the shared theme style sheets.
 *
 * Loaded by webpack in place of `webOverrides.ts` (which is an identity
 * function), so nothing here can reach Android, iOS or the tablet build.
 *
 * Scope: only the styles that several screens share, where a single value
 * fixes the same defect everywhere. Anything that would need a per-screen
 * value does not belong in this file - and anything that is not a layout
 * defect does not belong here either. This is not a restyle.
 */

import {
  webColor,
  webControl,
  webField,
  webModal,
} from '../platform/design/webTokens';

type StyleMap = Record<string, Record<string, unknown>>;

/**
 * Patches, keyed by the theme slice and then by the style name inside it.
 * Each patch is merged *over* the original values, so anything not named here
 * is left exactly as the shared sheet defines it.
 */
const patches: Record<string, StyleMap> = {
  /**
   * globalStyles.web.ts sets every input's caret to the brand primary, and
   * the sign-in field sits on a primary-coloured panel - so the caret was
   * drawn in the background colour and could not be seen. The field's text is
   * white there; the caret follows it. Inline style outranks the stylesheet.
   */
  signInStyle: {
    inputStyle: {
      caretColor: 'currentColor',
    },
    /*
     * On native the "email required" badge carries its own marginBottom: 20,
     * which pushes Next down. webZenInput pins every error slot to one fixed
     * line (no margins) so fields never jump - which left the badge 11px from
     * the button. The gap moves onto the button instead: it is there whether
     * or not the message is showing, so Next still never moves.
     */
    buttonStyle: {
      marginTop: 16,
    },
  },

  /**
   * `modalOuterView` + `innerView` is the app's modal shell: 14 screens build
   * their dialogs from this pair (quick product edit, modifier sets, add
   * customer, gift cards, park & retrieve, ...). The card itself has no size
   * ceiling of any kind - each screen picks a width inline
   * (`width: width > 900 ? '60%' : '80%'`) and nothing constrains height at
   * all, so a long form simply grows past the bottom of the browser window
   * and its footer buttons become unreachable.
   *
   * Constraining the shared card is what makes every one of those dialogs
   * consistent at once, and it is why this is a theme patch rather than 14
   * screen edits.
   */
  modifierSetStyle: {
    innerView: {
      /*
       * Percentage height keeps the card inside the viewport at any window
       * size, and `overflow: hidden` (already on innerView) plus the
       * ScrollView these dialogs already wrap their body in means the content
       * scrolls internally instead of the card overflowing. 13.8 / 13.11.
       */
      maxHeight: `${Math.round(webModal.maxHeightRatio * 100)}%`,
      /*
       * Absolute ceiling so a percentage width does not turn into a 1500px
       * dialog on a wide desktop monitor. Narrow windows are unaffected: the
       * screens' own percentage widths are already smaller than this.
       */
      maxWidth: webModal.maxWidth,
    },
  },

  /**
   * Add Product: the General Details and Price Details cards both use
   * `generalDetailsView`, which is `flex: 1`. Inside a column that is
   * itself pinned to the scroll viewport, `flex: 1` makes each card a
   * *share of the viewport* instead of the height of its own content, so
   * the content spills straight out of the card - measured at 1036x385
   * holding 753px of content, a 368px overhang that lands on top of the
   * card below. That is the "Product Unit Count" label sitting on the
   * seam between the two cards.
   *
   * `flex: 0` makes each card as tall as what it holds; the column then
   * grows and the ScrollView scrolls, which is what happens on Android
   * where the phone layout stacks these in a single column anyway.
   */
  addEditProductStyle: {
    generalDetailsView: {
      /*
       * Not `flex: 0`: react-native-web passes that straight through as the
       * CSS shorthand, and CSS `flex: 0` means flex-basis 0 - the cards
       * collapse to nothing and draw on top of each other. The longhands say
       * what is actually wanted, and the shorthand is cleared because RNW
       * skips null/undefined values outright.
       */
      flex: undefined,
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
    },

    /**
     * 14.3 - the currency prefix.
     *
     * The INR chip is `position: 'absolute'` with no offsets, so it lands on
     * its static position - which, because the field container carries
     * `paddingHorizontal: 10` (inputContainer10), is 10px in from the border.
     * Its rounded left corners therefore float in the middle of the field
     * instead of hugging it, and the 45px of `paddingLeft` that was supposed
     * to clear it lands at 55px against a chip that ends at 60px - so the
     * typed value starts *underneath* the prefix. Both effects are visible on
     * Wholesale Price and Cost Price in the screenshots.
     *
     * Taking the chip out of absolute positioning and letting it be the first
     * flex item is what makes it a real prefix: it reserves its own width, so
     * every price field gets the same prefix width, the same gutter and the
     * same text start, with no per-field padding to keep in sync.
     */
    leftIconContainerStyle: {
      position: 'relative',
      /*
       * height: '100%' resolves against a container whose own height is auto,
       * which is 'auto' in CSS - the chip would collapse to its text. Stretch
       * fills the cross axis whatever the row ends up being, and it beats the
       * `alignItems: center` the web ZenInput puts on the container.
       */
      height: undefined,
      alignSelf: 'stretch',
      width: 46,
      padding: 0,
      paddingRight: 0,
      /* zen-core's commonIconStyle insets the icon slot by 4px vertically. */
      marginVertical: 0,
      alignItems: 'center',
      justifyContent: 'center',
      borderTopLeftRadius: webControl.borderRadius,
      borderBottomLeftRadius: webControl.borderRadius,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      /* The divider between prefix and value, as in the spec's sketch. */
      borderRightWidth: webControl.borderWidth,
      borderRightColor: webColor.border,
    },

    /**
     * With the prefix in flow it has to start at the field border, so the
     * container's own horizontal padding goes; the input supplies the gutter
     * instead (inputPaddingLeft below). `overflow: hidden` makes the chip's
     * square right-hand corners follow the field's rounded outline.
     */
    inputContainer10: {
      paddingHorizontal: 0,
      paddingLeft: 0,
      paddingRight: 0,
      overflow: 'hidden',
    },

    /**
     * 45px of left padding existed only to clear the absolutely positioned
     * chip. The chip now reserves its own width, so this is an ordinary
     * gutter - the same one every other input on the page uses, which is what
     * puts the price value on the same x as the value in a plain field.
     */
    inputPaddingLeft: {
      height: webControl.height,
      paddingLeft: webControl.paddingHorizontal,
      paddingRight: webControl.paddingHorizontal,
      paddingHorizontal: webControl.paddingHorizontal,
    },

    /**
     * 14.4 - Availability Location spacing.
     *
     * Location / Stock / Low stock threshold are three fields in one column,
     * but only the middle one carries a margin (`marginTop: 20`), so the gap
     * above Stock is 20px while the gap above Low stock is 0 plus whatever
     * the validation slot happens to be. That uneven rhythm, and the empty
     * band it leaves under the dropdown, is the "excessive/uneven empty
     * space" in the screenshot. One shared row gap makes the three fields
     * read as one group.
     */
    stockContainer: {
      marginTop: webField.rowGap,
      /*
       * Same `flex: 1` story as the cards above: the Stock field block was
       * 1428x74 around 85px of content, so its reserved validation slot sat
       * 11px below its own box. Sizing to content puts it back inside.
       */
      flex: undefined,
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
    },

    /**
     * The Availability Location heading row, which holds the heading and the
     * + Add location button. It is flex: 1 inside a column, so once the card
     * around it sizes to its content the row is given a flex-basis of 0 and no
     * free space to grow into - it collapses to 0px and its two children hang
     * outside it. Measured at 1460x0. Sizing to content is the whole fix; the
     * row has exactly one call site, so nothing else can be affected.
     */
    rowContainer: {
      flex: undefined,
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
    },

    /** Same radius as every other card and control on the page. */
    locView: {
      borderRadius: webControl.borderRadius,
      paddingVertical: webField.rowGap,
    },
  },
};

export function applyWebStyleOverrides<T>(theme: T): T {
  const source = theme as unknown as Record<string, unknown>;
  const next: Record<string, unknown> = {...source};

  for (const [sliceName, slicePatch] of Object.entries(patches)) {
    const slice = source[sliceName] as Record<string, unknown> | undefined;
    if (!slice || typeof slice !== 'object') {
      continue;
    }

    const mergedSlice: Record<string, unknown> = {...slice};
    for (const [styleName, patch] of Object.entries(slicePatch)) {
      const original = slice[styleName];
      // Only merge into a real style object; never invent one that the shared
      // sheet does not define, so a rename upstream fails loudly instead of
      // silently growing a new style.
      if (original && typeof original === 'object') {
        mergedSlice[styleName] = {...(original as object), ...patch};
      }
    }
    next[sliceName] = mergedSlice;
  }

  return next as unknown as T;
}
